import { useRef, useState, useCallback } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import {
  Button,
  Spinner,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import {
  AddRegular,
  ArrowUndoRegular,
  ArrowSyncRegular,
  EraserRegular,
  SaveRegular,
} from '@fluentui/react-icons';
import { uploadImage } from '@moko/utils/src/uploadHelper';
import type { IUploadFileResponse } from '@moko/utils/src/interfaces/UploadFileInterfaces';
import { useOtherInfoContext } from '../OtherInfoContext';
import { FieldWrapper } from './FieldWrapper';
import type { FieldComponentProps } from './types';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  previewRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  fieldBox: {
    position: 'relative',
    display: 'inline-block',
    flex: 1,
    minWidth: 0,
    aspectRatio: '16 / 9',
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderBottomColor: tokens.colorNeutralForeground1,
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: 'hidden',
    '::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '2px',
      backgroundColor: tokens.colorBrandStroke1,
      transformOrigin: 'center',
      transform: 'scaleX(0)',
      transitionProperty: 'transform',
      transitionDuration: tokens.durationNormal,
      transitionTimingFunction: tokens.curveDecelerateMax,
    },
  },
  fieldBoxEditing: {
    width: '100%',
    borderBottomColor: 'transparent',
    '::after': {
      transform: 'scaleX(1)',
    },
  },
  canvas: {
    display: 'block',
    width: '100%',
    height: '100%',
    touchAction: 'none',
  },
  thumbnail: {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
  },
});

export const SignatureField = ({
  field,
  fieldValue,
  isFieldDisabled,
  validationMessage,
}: FieldComponentProps) => {
  const { handleOtherInfoChange } = useOtherInfoContext();
  const sigRef = useRef<SignatureCanvas | null>(null);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(false);
  const styles = useStyles();

  const handleClear = useCallback(() => {
    sigRef.current?.clear();
  }, []);

  const handleReset = useCallback(() => {
    setEditing(true);
  }, []);

  const handleCancel = useCallback(() => {
    sigRef.current?.clear();
    setEditing(false);
  }, []);

  const handleSave = useCallback(() => {
    if (!sigRef.current) return;

    if (sigRef.current.isEmpty()) {
      handleOtherInfoChange(field.name, '');
      sigRef.current.clear();
      setEditing(false);
      return;
    }

    const dataUrl = sigRef.current.getCanvas().toDataURL('image/jpeg');

    if (!navigator.onLine) {
      handleOtherInfoChange(field.name, dataUrl);
      sigRef.current?.clear();
      setEditing(false);
      return;
    }

    const abortController = new AbortController();
    setUploading(true);
    uploadImage(
      abortController,
      `signature_${Date.now()}.jpg`,
      'OtherInfo',
      dataUrl as unknown as ArrayBuffer
    )
      .then((data: IUploadFileResponse) => {
        handleOtherInfoChange(field.name, data.url);
        sigRef.current?.clear();
        setEditing(false);
      })
      .finally(() => setUploading(false));
  }, [field.name, handleOtherInfoChange]);

  return (
    <FieldWrapper field={field} validationMessage={validationMessage}>
      <div className={styles.root}>
        {!editing ? (
          <div className={styles.previewRow}>
            <div className={styles.fieldBox}>
              {fieldValue ? (
                <img
                  src={fieldValue}
                  alt={field.label}
                  className={styles.thumbnail}
                />
              ) : null}
            </div>
            {!isFieldDisabled && (
              <Button
                appearance="subtle"
                icon={fieldValue ? <ArrowSyncRegular /> : <AddRegular />}
                size="small"
                onClick={handleReset}
              >
                {fieldValue ? 'Reset' : 'Add'}
              </Button>
            )}
          </div>
        ) : (
          <div
            className={mergeClasses(styles.fieldBox, styles.fieldBoxEditing)}
          >
            <SignatureCanvas
              ref={sigRef}
              penColor="black"
              backgroundColor="rgb(255,255,255)"
              canvasProps={{
                className: styles.canvas,
              }}
            />
          </div>
        )}
        {editing && (
          <div className={styles.actions}>
            {uploading ? (
              <Spinner size="tiny" />
            ) : (
              <Button
                appearance="primary"
                icon={<SaveRegular />}
                size="small"
                onClick={handleSave}
              >
                Save
              </Button>
            )}
            <Button
              appearance="subtle"
              icon={<EraserRegular />}
              size="small"
              onClick={handleClear}
            >
              Clear
            </Button>
            <Button
              appearance="subtle"
              icon={<ArrowUndoRegular />}
              size="small"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    </FieldWrapper>
  );
};
